import React, {useEffect, useMemo, useState} from 'react';
import {Box, Paper, Tab, Tabs, Stack, Typography, useTheme, useMediaQuery} from '@mui/material';
import Api from 'src/api';
import dayjs from 'dayjs';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from 'src/redux/store';
import logbookApi from 'src/api/staffApi/logbookApi';
import {sabitlerKaydet} from 'src/redux/actions/appActions';
import {useNavigate} from 'react-router-dom';
import {showNotification} from 'src/redux/reducers/notificationReducer';
import {showConfirm} from 'src/redux/reducers/confirmReducer';
import {useFormik} from 'formik';
import * as Yup from 'yup';

import MenuYonetimi from './MenuYonetimi';
import KampanyaYonetimi from './KampanyaYonetimi';
import KahveYonetimi from './KahveYonetimi';
import HesapModal from './components/HesapModal';
import RolModal from './components/RolModal';
import UcretModal from './components/UcretModal';
import MaliHesaplarTab from './components/MaliHesaplarTab';
import RollerVeUcretlerTab from './components/RollerVeUcretlerTab';
import KullaniciRolleriTab from './components/KullaniciRolleriTab';
import CustomTabPanel from "src/components/CustomTabPanel";
import {Settings, AccountBalance, Badge, Group, RestaurantMenu, Campaign, Coffee} from '@mui/icons-material';

const accountSchema = Yup.object({
    hesapAdi: Yup.string().required('Hesap adı zorunludur'),
    hesapTipi: Yup.string().required('Hesap tipi zorunludur'),
    komisyon: Yup.number().min(0, 'Komisyon 0\'dan küçük olamaz').max(100, 'Komisyon 100\'den büyük olamaz'),
    kisi_id: Yup.mixed().when('hesapTipi', {
        is: (val: string) => val === 'Çalışan' || val === 'Yönetici',
        then: Yup.mixed().required('Bu hesap tipi için kişi seçimi zorunludur'),
    })
});

const roleSchema = Yup.object({
    rolAdi: Yup.string().required('Rol adı zorunludur'),
    rolTipi: Yup.string().required('Rol tipi zorunludur'),
});

const feeSchema = Yup.object({
    baslangic_tarih: Yup.string().required('Başlangıç tarihi zorunludur'),
    bitis_tarih: Yup.string().required('Bitiş tarihi zorunludur'),
    saat: Yup.number().min(0, 'Ücret 0\'dan küçük olamaz'),
    gun: Yup.number().min(0, 'Ücret 0\'dan küçük olamaz'),
    ay: Yup.number().min(0, 'Ücret 0\'dan küçük olamaz'),
    ekstra: Yup.number().min(0, 'Ücret 0\'dan küçük olamaz'),
});

const SistemYonetimi = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [roller, setRoller] = useState([]);
    const [ucretler, setUcretler] = useState([]);
    const [openHesapModal, setOpenHesapModal] = useState(false);
    const [openRolModal, setOpenRolModal] = useState(false);
    const [openUcretModal, setOpenUcretModal] = useState(false);
    const [activeRolForUcret, setActiveRolForUcret] = useState<any>(null);
    const [hesapPage, setHesapPage] = useState(0);
    const [hesapRowsPerPage, setHesapRowsPerPage] = useState(10);

    const sabitler = useSelector((state: RootState) => state.app.sabitler);
    const kisiler = useMemo(() => sabitler?.kisiler ?? [], [sabitler]);

    const hesaplar = useMemo(() => {
        if (!sabitler?.hesaplar) return [];
        const today = dayjs();
        const checkIsActive = (h: any) => {
            if (!h.aktif) return false;
            const baslangic = h.aktif_baslangic ? dayjs(h.aktif_baslangic) : null;
            const bitis = h.aktif_bitis ? dayjs(h.aktif_bitis) : null;
            if (baslangic && today.isBefore(baslangic, 'day')) return false;
            if (bitis && today.isAfter(bitis, 'day')) return false;
            return true;
        };
        const sorted = [...sabitler.hesaplar].sort((a, b) => {
            const aActive = checkIsActive(a);
            const bActive = checkIsActive(b);
            if (aActive !== bActive) return aActive ? -1 : 1;
            if (a.hesapTipi !== b.hesapTipi) return a.hesapTipi.localeCompare(b.hesapTipi);
            return a.hesapAdi.localeCompare(b.hesapAdi);
        });
        return sorted.map(h => ({...h, _rowIsActive: checkIsActive(h)}));
    }, [sabitler]);

    const accountFormik = useFormik({
        initialValues: {
            id: undefined, hesapAdi: '', hesapTipi: 'İşletme', aktif: true, komisyon: 0,
            aktif_baslangic: null, aktif_bitis: null, kisi_id: ''
        },
        validationSchema: accountSchema,
        onSubmit: async (values) => {
            try {
                if (values.id) await Api.putPromise('/logbook/yonetim/hesap/', values);
                else await Api.postPromise('/logbook/yonetim/hesap/', values);
                setOpenHesapModal(false);
                refreshConstants();
                dispatch(showNotification({message: "Hesap başarıyla kaydedildi.", severity: "success"}));
            } catch (error) {
            }
        }
    });

    const roleFormik = useFormik({
        initialValues: {id: undefined, rolAdi: '', rolTipi: ''},
        validationSchema: roleSchema,
        onSubmit: async (values) => {
            try {
                if (values.id) await Api.putPromise('/logbook/yonetim/rol/', values);
                else await Api.postPromise('/logbook/yonetim/rol/', values);
                setOpenRolModal(false);
                fetchRoller();
                dispatch(showNotification({message: "Rol başarıyla kaydedildi.", severity: "success"}));
            } catch (error) {
            }
        }
    });

    const feeFormik = useFormik({
        initialValues: {
            id: undefined, baslangic_tarih: '', bitis_tarih: '31-12-2099', saat: 0, gun: 0, ekstra: 0, ay: 0
        },
        validationSchema: feeSchema,
        onSubmit: async (values) => {
            try {
                const payload = {...values, rol: activeRolForUcret.id};
                if (values.id) await Api.putPromise('/logbook/yonetim/rolUcret/', payload);
                else await Api.postPromise('/logbook/yonetim/rolUcret/', payload);
                setOpenUcretModal(false);
                fetchUcretler(activeRolForUcret.id);
                dispatch(showNotification({message: "Ücret başarıyla kaydedildi.", severity: "success"}));
            } catch (error) {
            }
        }
    });

    const refreshConstants = async () => {
        try {
            const response = await logbookApi.getSabitler();
            dispatch(sabitlerKaydet(response));
        } catch (error) {
        }
    };

    const fetchRoller = async () => {
        try {
            const roles = await Api.getPromise('/logbook/yonetim/rol/');
            setRoller(roles);
        } catch (error) {
        }
    };

    const fetchUcretler = async (rolId: number) => {
        try {
            const data = await Api.getPromise('/logbook/yonetim/rolUcret/', {rolId});
            setUcretler(data);
        } catch (error) {
        }
    };

    useEffect(() => {
        fetchRoller();
    }, []);

    useEffect(() => {
        if (roller.length > 0 && !activeRolForUcret) {
            const firstRol: any = roller[0];
            setActiveRolForUcret(firstRol);
            fetchUcretler(firstRol.id);
        }
    }, [roller, activeRolForUcret]);

    const handleRolDelete = async (id: number) => {
        dispatch(showConfirm({
            message: "Bu rolü silmek istediğinize emin misiniz?",
            onConfirm: async () => {
                try {
                    await Api.deletePromise('/logbook/yonetim/rol/', {id});
                    fetchRoller();
                    if (activeRolForUcret?.id === id) setActiveRolForUcret(null);
                    dispatch(showNotification({message: "Rol silindi.", severity: "success"}));
                } catch (error) {
                }
            }
        }));
    };

    const handleUcretDelete = async (id: number) => {
        dispatch(showConfirm({
            message: "Bu ücret kaydını silmek istediğinize emin misiniz?",
            onConfirm: async () => {
                try {
                    await Api.deletePromise('/logbook/yonetim/rolUcret/', {id});
                    fetchUcretler(activeRolForUcret.id);
                    dispatch(showNotification({message: "Ücret kaydı silindi.", severity: "success"}));
                } catch (error) {
                }
            }
        }));
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Paper sx={{ width: '100%', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(_, val) => setTabValue(val)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            px: 2,
                            '& .MuiTab-root': { fontWeight: 700, minHeight: 64, textTransform: 'none', fontSize: '0.95rem' },
                            '& .Mui-selected': { color: 'primary.main' },
                            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' }
                        }}
                    >
                        <Tab icon={<AccountBalance sx={{ fontSize: 20 }} />} iconPosition="start" label="Mali Hesaplar"/>
                        <Tab icon={<Badge sx={{ fontSize: 20 }} />} iconPosition="start" label="Roller & Ücretler"/>
                        <Tab icon={<Group sx={{ fontSize: 20 }} />} iconPosition="start" label="Kullanıcı Rolleri"/>
                    </Tabs>
                </Box>

                <Box sx={{ p: 0 }}>
                    <CustomTabPanel value={tabValue} index={0}>
                        <MaliHesaplarTab
                            hesaplar={hesaplar}
                            hesapPage={hesapPage}
                            hesapRowsPerPage={hesapRowsPerPage}
                            onPageChange={(_, p) => setHesapPage(p)}
                            onRowsPerPageChange={(e) => {
                                setHesapRowsPerPage(+e.target.value);
                                setHesapPage(0);
                            }}
                            onNewHesap={() => {
                                accountFormik.resetForm();
                                setOpenHesapModal(true);
                            }}
                            onEditHesap={(row) => {
                                accountFormik.setValues({...row, kisi_id: row.sahibi_id || ''});
                                setOpenHesapModal(true);
                            }}
                            onRowClick={(row) => {
                                if (row.sahibi_id) navigate(`/logbook/yonetim/kisi/${row.sahibi_id}`);
                            }}
                        />
                    </CustomTabPanel>

                    <CustomTabPanel value={tabValue} index={1}>
                        <RollerVeUcretlerTab
                            roller={roller}
                            activeRol={activeRolForUcret}
                            ucretler={ucretler}
                            onNewRol={() => {
                                roleFormik.resetForm();
                                setOpenRolModal(true);
                            }}
                            onEditRol={(row) => {
                                roleFormik.setValues(row);
                                setOpenRolModal(true);
                            }}
                            onDeleteRol={handleRolDelete}
                            onRolClick={(row) => {
                                setActiveRolForUcret(row);
                                fetchUcretler(row.id);
                            }}
                            onNewUcret={() => {
                                feeFormik.resetForm();
                                setOpenUcretModal(true);
                            }}
                            onEditUcret={(row) => {
                                feeFormik.setValues(row);
                                setOpenUcretModal(true);
                            }}
                            onDeleteUcret={handleUcretDelete}
                        />
                    </CustomTabPanel>

                    <CustomTabPanel value={tabValue} index={2}>
                        <KullaniciRolleriTab />
                    </CustomTabPanel>
                </Box>
            </Paper>

            <HesapModal
                open={openHesapModal}
                onClose={() => setOpenHesapModal(false)}
                formik={accountFormik}
                kisiler={kisiler}
            />

            <RolModal
                open={openRolModal}
                onClose={() => setOpenRolModal(false)}
                formik={roleFormik}
            />

            <UcretModal
                open={openUcretModal}
                onClose={() => setOpenUcretModal(false)}
                formik={feeFormik}
                activeRol={activeRolForUcret}
            />
        </Box>
    );
};

export default SistemYonetimi;
